import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import ReportFields from './ReportFields';
import { type Package, failureMessage, request } from './service';

export default function AddApp() {
  const { fetchJWT } = useAuthContext();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const [results, setResults] = useState<{ packages: Package[]; more: boolean }>();
  const [selected, setSelected] = useState<Package | 'missing'>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setError('');
    setBusy(true);
    setResults(undefined);
    setSelected(undefined);
    try {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in to search apps.');
      const params = new URLSearchParams({
        catalogue: String(data.get('kind')),
        q: String(data.get('q')),
      });
      setResults(await request(idToken, `?${params}`));
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setBusy(false);
    }
  }
  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = Object.fromEntries(new FormData(event.currentTarget));
    setError('');
    setBusy(true);
    try {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Sign in to add an app.');
      const result = await request<{ id: string; existing: boolean }>(idToken, '', {
        ...data,
        action: 'add',
        kind: selected === 'missing' ? 'missing' : selected?.kind,
        token: selected === 'missing' ? '' : selected?.token,
      });
      await cache.invalidateQueries({ queryKey: ['homebrew'] });
      navigate(`/homebrew/apps/${result.id}${result.existing ? '?existing=1' : ''}`);
    } catch (err) {
      setError(failureMessage(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Link className="govuk-back-link" to="/homebrew/library">
        App Library
      </Link>
      <h1 className="govuk-heading-l">Find or add an app</h1>
      <p className="govuk-body">
        Search Homebrew first. If you cannot find the software you need, record the gap so others
        can add their needs and advice.
      </p>
      {error && (
        <p className="govuk-error-message" role="alert">
          {error}
        </p>
      )}
      <form onSubmit={search}>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="package-query">
            App or tool name
          </label>
          <input
            className="govuk-input"
            id="package-query"
            name="q"
            minLength={2}
            maxLength={100}
            required
          />
        </div>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="package-kind">
            Software type
          </label>
          <select className="govuk-select" id="package-kind" name="kind">
            <option value="cask">Desktop apps (Homebrew casks)</option>
            <option value="formula">Command-line tools (Homebrew formulae)</option>
          </select>
        </div>
        <button className="govuk-button" type="submit" disabled={busy}>
          Search Homebrew
        </button>
      </form>
      {busy && (
        <p className="govuk-body" role="status">
          Working...
        </p>
      )}
      {results && (
        <div aria-live="polite">
          <h2 className="govuk-heading-m">Homebrew results</h2>
          <p className="govuk-body">
            {results.packages.length
              ? 'Choose the matching package.'
              : 'No matching package found. Try another name or software type, or record missing software below.'}
          </p>
          <ul className="govuk-list homebrew-app-list">
            {results.packages.map((pkg) => (
              <li key={pkg.token}>
                <h3 className="govuk-heading-s">
                  {pkg.name} <span className="govuk-caption-m">{pkg.token}</span>
                </h3>
                <p className="govuk-body">{pkg.description}</p>
                <p className="govuk-body">
                  Homebrew: {pkg.availability}
                  {pkg.reason && ` (${pkg.reason})`}
                </p>
                {pkg.command && (
                  <p className="govuk-body">
                    <code>{pkg.command}</code>
                  </p>
                )}
                <p className="govuk-body">
                  <a className="govuk-link" href={pkg.source} target="_blank" rel="noreferrer">
                    Homebrew requirements and details (opens in new tab)
                  </a>
                </p>
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary"
                  onClick={() => setSelected(pkg)}
                >
                  Choose {pkg.name}
                </button>
              </li>
            ))}
          </ul>
          {results.more && (
            <p className="govuk-body">
              Showing the first 30 results. Refine your search for more specific matches.
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        className="govuk-button govuk-button--secondary"
        disabled={busy}
        onClick={() => setSelected('missing')}
      >
        Record software without a Homebrew package
      </button>
      {selected && (
        <form onSubmit={add} className="homebrew-add-form">
          <h2 className="govuk-heading-m">
            {selected === 'missing' ? 'Record missing software' : `Add ${selected.name}`}
          </h2>
          {selected === 'missing' && (
            <>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="app-name">
                  Software name
                </label>
                <input className="govuk-input" id="app-name" name="name" maxLength={160} required />
              </div>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="description">
                  Description (optional)
                </label>
                <textarea
                  className="govuk-textarea"
                  id="description"
                  name="description"
                  maxLength={1000}
                  rows={2}
                />
              </div>
            </>
          )}
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="purpose">
              What do you need this software for?
            </label>
            <p className="govuk-hint" id="purpose-hint">
              Explain the task it supports, including software you used before moving to a MacBook.
            </p>
            <textarea
              className="govuk-textarea"
              id="purpose"
              name="purpose"
              aria-describedby="purpose-hint"
              maxLength={2000}
              rows={3}
              required
            />
          </div>
          <ReportFields required={false} />
          <p className="govuk-body-s">
            Your email and time posted will be recorded. Adding an app also adds your vote and puts
            it in My Apps.
          </p>
          <button className="govuk-button" type="submit" disabled={busy}>
            Add app
          </button>
        </form>
      )}
    </>
  );
}
